import {
  SetStateAction, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { Link, Outlet, useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import type { AccountBrand, FieldDefinition, FormLayout, FormRule, FormWidget } from '../../shared/types';
import { normalizeRules } from '../../shared/types';
import type { KeyRename } from '../../shared/documentRename';
import {
  FormActivity, FormDetail, FormThemeSettings, FormVersionDetail, formChanges, getForm, getFormBuilder, getFormVersion,
  publishForm, restoreVersionDocument, saveFormDraft,
} from '../../api/forms';
import { adminMe } from '../../api/admin';
import type { Level } from '../../api/access';
import ShareDialog from './ShareDialog';
import DistributeDialog from './DistributeDialog';
import { PlanLimitBody, planLimitOf } from '../../api/limits';
import { useToast } from '../../components/Toast';
import { useUnsavedGuard } from '../../components/unsaved';
import { ActionBar, Modal, RowMenu } from '../../components/fs';
import { PHONE, useMediaQuery } from '../../shared/media';
import type { ApiRequestError } from '../../api/client';
import { type CommitOptions, useHistory } from '../../shared/useHistory';
import FormAssistant from './FormAssistant';
import FormBar, { UnpublishedIndicator } from './FormBar';
import PublishDrawer, { type ChangeSummary } from './PublishDrawer';
import {
  BField, BSection, defaultStyle, isStructural, placeItems, splitDuplicateCards, toFieldBody, toLayout, toWidgets,
  withCardMarkers, withDraftCards,
} from './builderModel';

/**
 * Everything a respondent can see, held as ONE working copy.
 *
 * Layout, fields, widgets, rules and presentation are all parts of a single
 * form, so they share a single draft and a single Publish. Logic used to save
 * straight to the live form, which meant deleting a rule changed the form a
 * customer was filling in at that moment — and a rule written against a
 * draft-only field went live pointing at a field the public form did not have.
 */
export interface FormDraft {
  sections: BSection[];
  fields: BField[];
  widgets: FormWidget[];
  rules: FormRule[];
  theme: FormThemeSettings;
  /** the label on the public form's submit button ('' = the default) */
  submitLabel: string;
  /**
   * Field keys renamed in this draft since the last publish, oldest first
   * (R5). The document — placements, regions, the AcroForm mapping, the
   * parts — names fields by key and saves live, outside the draft, so a
   * rename cannot be written into it until the new key is live: the publish
   * route applies these in the same transaction. Discarding the draft drops
   * them with it, and the document stays as it was.
   */
  renames: KeyRename[];
}

/**
 * Where the WORKING COPY stands, and nothing else: clean = it matches the
 * definition the server holds · draft = a draft of it is stored, unpublished ·
 * dirty = edits that have not been saved yet.
 *
 * It says nothing about whether the form is live, because it cannot: a form
 * that has never been published has no draft either, and this used to read
 * `published` for it. Publication is `form.status` — draft, published,
 * archived — which the form bar shows beside the form's name.
 */
export type DraftState = 'clean' | 'draft' | 'dirty';

/**
 * A part setter with the useState signature, plus how the change should sit in
 * the undo history: `coalesce` folds it into the previous step under the same
 * key (typing a label is one step, not one per letter).
 */
export type PartSetter<T> = (v: SetStateAction<T>, opts?: CommitOptions) => void;

/**
 * What the autosave is doing right now. `state` says whether anything is
 * unsaved; this says why the pill reads the way it does.
 */
export type SaveState =
  | { kind: 'idle' }
  /** a change landed; the save fires after the debounce */
  | { kind: 'pending' }
  | { kind: 'saving' }
  | { kind: 'saved'; at: number }
  | { kind: 'failed'; message: string }
  /** the server holds a newer draft than the one this session last saw */
  | { kind: 'conflict'; theirs: string | null };

export interface DraftHistory {
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
}

/** How long after the last change the draft saves itself. */
export const AUTOSAVE_MS = 2000;
/** Keystrokes closer than this, on the same control, are one undo step. */
export const COALESCE_MS = 500;
/** The most undo steps kept. */
export const HISTORY_CAP = 100;

export interface FormWorkspaceContext {
  form: FormDetail;
  refreshForm: () => Promise<void>;
  /**
   * The Document tab changed the draft's document on the server (a save, a
   * PDF added or removed). The form now has unpublished changes even if the
   * builder's working copy is untouched, and the Publish panel's list wants
   * reading again.
   */
  noteDocumentDraft: () => void;
  /**
   * A tab with a save still on its way registers how to finish it; Publish
   * waits for every one of them first. The Workflow tab saves its drawing a
   * moment after the last change, and a Publish pressed inside that moment
   * must not go out with the drawing from before it. Returns the un-register.
   */
  registerBeforePublish: (finish: () => Promise<void>) => () => void;
  /**
   * Bumped whenever the draft's document was replaced from OUTSIDE the
   * Document tab — a discard, a version restore, another session's draft
   * loaded. The tab keeps its own working state, and remounts on this.
   */
  documentEpoch: number;
  /** The plan features in force — plan-gated UI renders upsell states from this. */
  features: { sso: boolean; api: boolean; customBranding: boolean; hideAdvertisement: boolean; smartPdf: boolean };
  /**
   * The account's brand, when it has one — what the draft's theme resolves
   * AGAINST for rendering, and never into. It is deliberately not part of
   * `draft`: the draft is what Publish writes, and folding inherited values
   * into it would save them as this form's own overrides and quietly take the
   * form out of the brand. Undefined until B3 gives an account a way to set
   * one.
   */
  brand?: AccountBrand;
  /**
   * Replace the account's brand in place, after something wrote one.
   *
   * The builder's "use this form's branding for the account" is the only
   * caller: it changes the brand the canvas resolves against, and re-fetching
   * the whole builder payload to learn what it just sent would repaint the
   * canvas and throw away the draft's undo history.
   */
  setBrand: (b: AccountBrand) => void;

  /** null while the builder payload is still loading */
  draft: FormDraft | null;
  /** Per-part setters with the useState signature, so every edit marks dirty. */
  setSections: PartSetter<BSection[]>;
  setFields: PartSetter<BField[]>;
  setWidgets: PartSetter<FormWidget[]>;
  setRules: PartSetter<FormRule[]>;
  setTheme: PartSetter<FormThemeSettings>;
  setSubmitLabel: PartSetter<string>;
  /** The key renames recorded on this draft (R5) — the builder's key commit appends to them. */
  setRenames: PartSetter<KeyRename[]>;
  /** Load a whole definition in at once (version restore) as unsaved changes. */
  replaceDraft: (next: FormDraft) => void;
  /** Undo and redo over the whole working copy. */
  history: DraftHistory;

  state: DraftState;
  save: SaveState;
  busy: boolean;
  /** the 402 body from a publish refused for fieldsPerForm, until one succeeds */
  limitHit: PlanLimitBody | null;

  /** the latest publish's ordinal (R3); 0 until the first publish */
  version: number;
  /** the form's activity (R3): the "{n} new" badge reads `new` */
  activity: FormActivity | null;
  /**
   * Load version `v` into the draft as unsaved changes, and put its document
   * back at once (R3). Resolves once both have happened.
   */
  restoreVersion: (v: number) => Promise<void>;
  /** The live form's address, or null until the form is published (the bar's Preview ↗; Test mode's live test, R9). */
  publicUrl: string | null;
}

const EMPTY: FormDraft = { sections: [], fields: [], widgets: [], rules: [], theme: {}, submitLabel: '', renames: [] };

/** A published layout as canvas sections. */
function fromLayout(layout: FormLayout): BSection[] {
  return (layout.sections || [])
    .slice().sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
    .map((s) => ({
      ...s,
      cards: (s.cards || []).slice().sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
        .map((c) => ({
          id: c.id, title: c.title, scope: c.scope || 'order',
          style: { ...defaultStyle(), ...(c.style || {}) },
          ...(c.hidden ? { hidden: true } : {}),
        })),
      style: { ...defaultStyle(), ...(s.style || {}) },
    })) as BSection[];
}

/**
 * A published definition as a canvas draft. Cards come back as markers in the
 * field spine — where a card sits is one question the canvas answers in one
 * place, so a definition has to be unpacked into that shape before it can be
 * edited (and a draft saved before cards could be placed gets its markers here
 * on the way in, exactly where those cards used to render).
 */
const canvasOf = (layout: FormLayout, fields: FieldDefinition[], widgets: FormWidget[]) =>
  // Two cards sharing an id both claim the fields grouped under it, so the
  // duplicate is given an id of its own before the canvas reads either.
  splitDuplicateCards(fromLayout(layout), withCardMarkers(layout, fields, widgets));

/** The tabs that edit the form definition — the only two the draft serves. */
const DEFINITION_TABS = ['build', 'logic'];
/**
 * The tabs that draw their own frame — a canvas with a rail and an inspector
 * (R4, R5, R6), a table with a locked column (R6, R8), a page with its own
 * aside (R7). Every OTHER tab is wrapped in `.fw-page`: the design's fluid page
 * padding, edge to edge, no maximum width and no centring — so a new tab lands
 * in the frame the design draws without opting in (playbook §1a). A tab that
 * needs the whole viewport, or its own padding, joins this list. There is no
 * centred column to fall back into: R14 deleted `.ad-body`.
 */
const SELF_FRAMED_TABS = ['build', 'logic', 'document', 'workflow', 'runs', 'submissions', 'access', 'invitations'];

/** Per-form workspace: the form bar (R3) over Fields / Logic / Document / Workflow / Link & access / Invitations / Responses / Summary / Analytics / Form settings / Versions. */
export default function FormWorkspace() {
  const { formId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  /** The table's filters, kept when stepping between Responses and Summary. */
  const dataSearch = /\/(submissions|summary)(\/|$)/.test(location.pathname) ? location.search : '';
  const [form, setForm] = useState<FormDetail | null>(null);
  /** The latest publish's ordinal (R3), from the form API. */
  const [version, setVersion] = useState(0);
  const [activity, setActivity] = useState<FormActivity | null>(null);
  /**
   * What THIS person may do here.
   *
   * A View grant reaches the form and its responses and nothing else, so the
   * tabs that change it are absent rather than disabled — the same shape
   * SettingsLayout already uses for Billing, and the same reasoning: a door
   * that will not open is worse than no door.
   */
  const [myLevel, setMyLevel] = useState<Level | null>(null);
  /** Set when the server said this form exists here but is not shared (3D). */
  const [noAccess, setNoAccess] = useState<{ formName: string } | null>(null);
  /** Who can reach this — opened from the header, per canvas 4. */
  const [sharing, setSharing] = useState(false);
  /** The public link, QR and embed snippets (U6) — respondent distribution, not team access. */
  const [distributing, setDistributing] = useState(false);
  const [accountSlug, setAccountSlug] = useState('');
  const [publicBaseUrl, setPublicBaseUrl] = useState<string | null>(null);
  const [platformBaseUrl, setPlatformBaseUrl] = useState<string | null>(null);
  const [features, setFeatures] = useState({ sso: false, api: false, customBranding: false, hideAdvertisement: false, smartPdf: false });
  // Held beside the draft, never inside it — see FormWorkspaceContext.brand.
  const [brand, setBrand] = useState<AccountBrand | undefined>(undefined);
  const [error, setError] = useState('');

  const [draft, setDraft] = useState<FormDraft | null>(null);
  const [state, setState] = useState<DraftState>('clean');
  const [save, setSave] = useState<SaveState>({ kind: 'idle' });
  /** The conflict dialog; the pill reopens it once it has been dismissed. */
  const [conflictOpen, setConflictOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [limitHit, setLimitHit] = useState<PlanLimitBody | null>(null);
  /** The Publish panel (R3). */
  const [publishOpen, setPublishOpen] = useState(false);
  /**
   * What publishing would change, as the server last worked it out (R3):
   * refreshed after every save and whenever the panel opens, cleared to zero
   * by a publish or a discard. Null until the first answer.
   */
  const [changes, setChanges] = useState<ChangeSummary | null>(null);
  const [changesLoading, setChangesLoading] = useState(false);
  const [changesError, setChangesError] = useState('');
  const toast = useToast();

  /**
   * The working copy as a ref as well as state: an autosave timer and a
   * setter called twice in one handler both need the LATEST draft, not the
   * one the render they were created in saw.
   */
  const draftRef = useRef<FormDraft | null>(null);
  /** Whether anything has changed since the last save reached the server. */
  const dirtyRef = useRef(false);
  /** The `draft_updated_at` this session last saw — echoed on every save. */
  const seenRef = useRef<string | null>(null);
  const timer = useRef<number | null>(null);
  /** The save in flight, so a second one waits its turn instead of racing it. */
  const inFlight = useRef<Promise<void> | null>(null);
  const history = useHistory<FormDraft>({ cap: HISTORY_CAP, coalesceMs: COALESCE_MS });
  // The stable half — the whole object changes identity on every step.
  const historyActions = history.actions;
  /** Changes made in one synchronous handler are one undo step (see `commit`). */
  const tick = useRef({ seq: 0, open: false });

  const refreshForm = useCallback(async () => {
    if (!formId) return;
    try {
      const r = await getForm(formId);
      setForm(r.form);
      setMyLevel(r.myLevel);
      if (typeof r.version === 'number') setVersion(r.version);
      if (r.activity) setActivity(r.activity);
    } catch (e: any) {
      const body = e?.body as { reason?: string; formName?: string } | undefined;
      if (body?.reason === 'no_access') setNoAccess({ formName: body.formName || 'This form' });
      else setError(e.message);
    }
  }, [formId]);

  useEffect(() => { refreshForm(); }, [refreshForm]);
  useEffect(() => {
    adminMe().then((r) => {
      setAccountSlug(r.account?.slug || '');
      setPublicBaseUrl(r.formLinkBaseUrl || r.publicBaseUrl || null);
      setPlatformBaseUrl(r.publicBaseUrl || null);
      if (r.features) setFeatures(r.features);
    }).catch(() => undefined);
  }, []);

  /**
   * Ask the server what publishing `snapshot` would change (R3). The answer
   * is only kept while it is still about the latest working copy a save has
   * seen; a stale one is dropped rather than shown.
   */
  const refreshChanges = useCallback(async (snapshot: FormDraft | null) => {
    if (!snapshot || !formId) return;
    setChangesLoading(true);
    try {
      const r = await formChanges(formId, snapshot as unknown as Record<string, unknown>);
      if (typeof r.count !== 'number' || !Array.isArray(r.changes)) throw new Error('The change list did not arrive.');
      setChanges({ since: r.since, count: r.count, list: r.changes });
      setChangesError('');
    } catch (e: any) {
      setChangesError(e?.message || 'Could not work out what changed.');
    } finally {
      setChangesLoading(false);
    }
  }, [formId]);
  const refreshChangesRef = useRef(refreshChanges);
  refreshChangesRef.current = refreshChanges;

  const beforePublish = useRef(new Set<() => Promise<void>>());
  const registerBeforePublish = useCallback((finish: () => Promise<void>) => {
    beforePublish.current.add(finish);
    return () => { beforePublish.current.delete(finish); };
  }, []);

  const [documentEpoch, setDocumentEpoch] = useState(0);
  const noteDocumentDraft = useCallback(() => {
    // A clean form becomes a draft; one already dirty or drafted stays what it is.
    setState((s) => (s === 'clean' ? 'draft' : s));
    void refreshChangesRef.current(draftRef.current);
  }, []);

  /**
   * Take a builder payload as the working copy. A stored draft wins over the
   * published definition part by part, so a draft saved before rules and
   * theme joined it still opens with the live ones alongside its canvas.
   * Also the way back after a conflict: "Reload their version" is this.
   */
  const adoptBuilder = useCallback((b: Awaited<ReturnType<typeof getFormBuilder>>) => {
    const d = (b.draft || null) as Partial<FormDraft> | null;
    const hasCanvas = !!(d && Array.isArray(d.sections) && Array.isArray(d.fields));
    const widgets = (hasCanvas && Array.isArray(d!.widgets) ? d!.widgets : b.widgets) || [];
    const canvas = hasCanvas
      ? withDraftCards(d!.sections as BSection[], d!.fields as BField[], widgets)
      : canvasOf(b.layout, b.fields, b.widgets || []);
    const next: FormDraft = {
      sections: canvas.sections,
      fields: canvas.fields,
      widgets,
      rules: normalizeRules(d && Array.isArray(d.rules) ? d.rules : b.rules),
      theme: (d && d.theme ? d.theme : b.form.settings?.theme) || {},
      submitLabel: (d && typeof d.submitLabel === 'string' ? d.submitLabel : b.form.settings?.submitLabel) || '',
      // Only a stored draft carries renames; a clean form has renamed nothing.
      renames: d && Array.isArray(d.renames)
        ? d.renames.filter((r): r is KeyRename => !!r && typeof r.from === 'string' && typeof r.to === 'string')
        : [],
    };
    if (timer.current) { window.clearTimeout(timer.current); timer.current = null; }
    draftRef.current = next;
    dirtyRef.current = false;
    seenRef.current = b.form.draft_updated_at ?? null;
    setBrand(b.brand);
    setDraft(next);
    historyActions.reset(next);
    setState(d ? 'draft' : 'clean');
    setSave({ kind: 'idle' });
    setConflictOpen(false);
    setSaveError('');
    // A stored draft has changes worth counting; a clean form has none.
    if (d) void refreshChangesRef.current(next);
    else setChanges((c) => (c ? { ...c, count: 0, list: [] } : c));
  }, [historyActions]);

  // The working copy, loaded once for the whole workspace.
  useEffect(() => {
    if (!formId) return;
    let live = true;
    getFormBuilder(formId)
      .then((b) => { if (live) adoptBuilder(b); })
      .catch((e) => { if (live) setError(e.message || 'Failed to load the builder.'); });
    return () => { live = false; };
  }, [formId, adoptBuilder]);

  const cancelTimer = () => { if (timer.current) { window.clearTimeout(timer.current); timer.current = null; } };

  /**
   * One save at a time, always of the LATEST draft. A save that starts while
   * another is in flight waits for it; a draft that changed while its save
   * was in flight is still dirty afterwards and schedules the next one.
   * Manual saves toast; the autosave speaks only through the pill.
   */
  const runSave = useCallback(async (manual: boolean): Promise<void> => {
    cancelTimer();
    if (inFlight.current) { try { await inFlight.current; } catch { /* it reported itself */ } }
    const snapshot = draftRef.current;
    if (!snapshot || !formId || !dirtyRef.current) return;
    setSave({ kind: 'saving' });
    setSaveError('');
    const p = saveFormDraft(formId, snapshot as unknown as Record<string, unknown>, seenRef.current)
      .then((r) => {
        seenRef.current = r.draftUpdatedAt ?? null;
        if (draftRef.current === snapshot) { dirtyRef.current = false; setState('draft'); }
        else scheduleRef.current();
        setSave({ kind: 'saved', at: Date.now() });
        if (manual) {
          toast('Draft saved — respondents still see the published form.');
          refreshForm().catch(() => undefined);
        }
        // What the bar's "{n} unpublished" counts (R3): the draft as just saved.
        void refreshChangesRef.current(snapshot);
      })
      .catch((e: unknown) => {
        const err = e as ApiRequestError;
        if (err.status === 409) {
          const theirs = (err.body as { draftUpdatedAt?: string | null } | undefined)?.draftUpdatedAt ?? null;
          setSave({ kind: 'conflict', theirs });
          setConflictOpen(true);
        } else {
          setSave({ kind: 'failed', message: err.message || 'Draft save failed.' });
        }
        throw e;   // so "Save draft and leave" stays put rather than losing it
      })
      .finally(() => { inFlight.current = null; });
    inFlight.current = p;
    await p;
  }, [formId, toast, refreshForm]);

  /** Arm the autosave: two quiet seconds after the last change, it saves. */
  const schedule = useCallback(() => {
    cancelTimer();
    setSave((s) => (s.kind === 'saving' ? s : { kind: 'pending' }));
    timer.current = window.setTimeout(() => { void runSaveRef.current(false).catch(() => undefined); }, AUTOSAVE_MS);
  }, []);
  const runSaveRef = useRef(runSave);
  runSaveRef.current = runSave;
  const scheduleRef = useRef(schedule);
  scheduleRef.current = schedule;
  useEffect(() => cancelTimer, []);

  /**
   * Every change lands here: the ref and the state move together, the step is
   * recorded, the form is dirty, and the autosave is armed.
   *
   * A handler that calls two setters (deleting a card touches sections AND
   * fields) is one thing the person did, so pushes with no key of their own
   * share a key for the rest of the current tick and fold into one step.
   */
  const commit = useCallback((next: FormDraft, opts?: CommitOptions) => {
    draftRef.current = next;
    dirtyRef.current = true;
    let o = opts;
    if (!o?.coalesce) {
      if (!tick.current.open) {
        tick.current = { seq: tick.current.seq + 1, open: true };
        void Promise.resolve().then(() => { tick.current.open = false; historyActions.settle(); });
      }
      o = { ...o, coalesce: `tick:${tick.current.seq}` };
    }
    historyActions.push(next, o);
    setDraft(next);
    setState('dirty');
    setSaveError('');
    schedule();
  }, [historyActions, schedule]);

  /**
   * One setter per part, each with the useState signature the editors already
   * use — so an edit anywhere marks the whole form dirty without every call
   * site having to say so.
   */
  const setters = useMemo(() => {
    const part = <K extends keyof FormDraft>(k: K): PartSetter<FormDraft[K]> => (v, opts) => {
      const d = draftRef.current;
      if (!d) return;
      const value = typeof v === 'function' ? (v as (p: FormDraft[K]) => FormDraft[K])(d[k]) : v;
      commit({ ...d, [k]: value }, opts);
    };
    return {
      setSections: part('sections'),
      setFields: part('fields'),
      setWidgets: part('widgets'),
      setRules: part('rules'),
      setTheme: part('theme'),
      setSubmitLabel: part('submitLabel'),
      setRenames: part('renames'),
    };
  }, [commit]);

  const replaceDraft = useCallback((next: FormDraft) => {
    commit(next);
  }, [commit]);

  /** Step through the history. An undone state is a change like any other: dirty, and saved on the timer. */
  const step = useCallback((next: FormDraft | null) => {
    if (!next) return;
    draftRef.current = next;
    dirtyRef.current = true;
    setDraft(next);
    setState('dirty');
    setSaveError('');
    schedule();
  }, [schedule]);
  const undo = useCallback(() => step(historyActions.undo()), [step, historyActions]);
  const redo = useCallback(() => step(historyActions.redo()), [step, historyActions]);
  const draftHistory = useMemo<DraftHistory>(
    () => ({ undo, redo, canUndo: history.canUndo, canRedo: history.canRedo }),
    [undo, redo, history.canUndo, history.canRedo],
  );

  /** The manual save: what the button, the guard, Ctrl+S and "Keep mine" run. */
  const saveDraft = () => runSave(true);

  /** Take their draft and drop this session's unsaved changes. */
  const reloadTheirs = useCallback(async () => {
    if (!form) return;
    setBusy(true);
    try {
      adoptBuilder(await getFormBuilder(form.id));
      await refreshForm().catch(() => undefined);
      setDocumentEpoch((n) => n + 1);
      toast('Loaded the draft saved by the other session.', { kind: 'info' });
    } catch (e: any) { setSaveError(e.message || 'Could not reload the draft.'); }
    finally { setBusy(false); }
  }, [form, adoptBuilder, toast, refreshForm]);

  /** Write this session's draft over theirs, by saying we have seen theirs. */
  const keepMine = useCallback(() => {
    if (save.kind !== 'conflict') return;
    seenRef.current = save.theirs;
    dirtyRef.current = true;
    setConflictOpen(false);
    setState('dirty');
    void runSave(true).catch(() => undefined);
  }, [save, runSave]);

  async function publish() {
    if (!draft || !form) return;
    setBusy(true); setSaveError('');
    // Publish clears the stored draft; an autosave landing after it would put
    // the draft straight back. Nothing may be in flight when the POST goes.
    cancelTimer();
    // …including a tab's own save (the Workflow tab's drawing): finished first,
    // and a failure there is theirs to show, not a reason to publish without it.
    try { await Promise.all([...beforePublish.current].map((finish) => finish())); }
    catch (e: any) { setSaveError(e?.message || 'The last change could not be saved, so nothing was published.'); setBusy(false); return; }
    if (inFlight.current) { try { await inFlight.current; } catch { /* reported already */ } }
    try {
      const { fieldOrder } = placeItems(draft.fields);
      const r = await publishForm(form.id, {
        layout: toLayout(draft.sections, draft.fields),
        // isStructural, not a hand-written slot/card pair: a widget container's
        // marker is structural too, and publishing it turned it into a real
        // field that shared its key with the marker the canvas rebuilds.
        fields: draft.fields.filter((f) => !isStructural(f))
          .map((f, i) => toFieldBody(f, fieldOrder.get(f.key) ?? (i + 1) * 10)),
        rules: draft.rules,
        widgets: toWidgets(draft.widgets, draft.fields),
        settings: { theme: draft.theme, submitLabel: draft.submitLabel },
        // The document follows the renamed keys in the same transaction (R5).
        ...(draft.renames.length ? { renames: draft.renames } : {}),
      });
      dirtyRef.current = false;
      seenRef.current = null;
      setState('clean');
      setSave({ kind: 'idle' });
      setLimitHit(null);
      setPublishOpen(false);
      if (typeof r.version === 'number') setVersion(r.version);
      setChanges({ since: typeof r.version === 'number' ? r.version : version + 1, count: 0, list: [] });
      toast('Published — the public form is live with these changes.');
      refreshForm().catch(() => undefined);
    } catch (e: any) {
      const limit = planLimitOf(e);
      if (limit && limit.limit === 'fieldsPerForm') {
        setLimitHit(limit);
        // The Fields tab draws the refusal itself — the block, the counter
        // and the inert add controls — so the panel gets out of its way
        // there; on every other tab the panel is where the block shows.
        if (/\/build(\/|$)/.test(location.pathname)) setPublishOpen(false);
      } else setSaveError(e.message || 'Publish failed.');
    }
    finally { setBusy(false); }
  }

  /** Throw the working copy away and reload whatever is published. */
  async function discard() {
    if (!form) return;
    setBusy(true); setSaveError('');
    cancelTimer();
    if (inFlight.current) { try { await inFlight.current; } catch { /* reported already */ } }
    try {
      await saveFormDraft(form.id, null, seenRef.current);
      adoptBuilder(await getFormBuilder(form.id));
      setLimitHit(null);
      setPublishOpen(false);
      toast('Draft discarded — back to what is published.', { kind: 'info' });
      // The document went with the draft; the Document tab starts again from the live one.
      await refreshForm().catch(() => undefined);
      setDocumentEpoch((n) => n + 1);
    } catch (e: any) {
      // Discarding over a draft somebody else saved since is the same
      // question as saving over it, and gets the same dialog.
      if ((e as ApiRequestError).status === 409) {
        setSave({ kind: 'conflict', theirs: (e.body as { draftUpdatedAt?: string | null } | undefined)?.draftUpdatedAt ?? null });
        setConflictOpen(true);
      } else setSaveError(e.message || 'Could not discard the draft.');
    }
    finally { setBusy(false); }
  }

  const restoreDefinition = useCallback((def: FormVersionDetail['definition']) => {
    replaceDraft({
      ...EMPTY,
      ...canvasOf(def.layout, def.fields || [], def.widgets || []),
      widgets: def.widgets || [],
      rules: normalizeRules(def.rules),
      theme: draft?.theme || {},
      submitLabel: draft?.submitLabel || '',
      // A restored definition brings its own keys; renames made against the
      // draft it replaces would rename the document toward keys it may not have.
      renames: [],
    });
  }, [replaceDraft, draft?.theme, draft?.submitLabel]);

  /**
   * Version restore (R3): the definition into the draft as unsaved changes,
   * the document — which never rides the draft — back at once, through the
   * route that writes it the way the Document tab does.
   */
  const restoreVersion = useCallback(async (v: number) => {
    if (!form) return;
    setBusy(true); setSaveError('');
    try {
      const r = await getFormVersion(form.id, v);
      restoreDefinition(r.version.definition);
      setPublishOpen(false);
      if (r.version.definition.document) {
        const d = await restoreVersionDocument(form.id, v);
        await refreshForm();
        if (d.restored) { setDocumentEpoch((n) => n + 1); noteDocumentDraft(); }
        // Its document is in the draft too now — it used to go live at once.
        toast(d.restored
          ? `Version ${v} is in your draft as unsaved changes, with its document. Publish to make it live.`
          : `Version ${v} is in your draft as unsaved changes.`);
      } else {
        toast(`Version ${v} is in your draft as unsaved changes.`);
      }
    } catch (e: any) {
      setSaveError(e.message || 'Restore failed.');
      throw e;
    } finally {
      setBusy(false);
    }
  }, [form, restoreDefinition, refreshForm, toast]);

  /**
   * Moving between this form's own tabs is safe — the working copy lives here,
   * above them — so the guard fires only at the workspace boundary: a link out
   * of the form, or a reload. Each tab that saves something of its OWN runs a
   * guard of its own for the same reason.
   */
  // D4: on a phone the bar is a different branch, and the draft bar moves to a
  // sticky bar at the bottom of the page. Read before the early returns so the
  // hook order never changes.
  const phone = useMediaQuery(PHONE);

  const guard = useUnsavedGuard({
    dirty: state === 'dirty',
    within: `/admin/forms/${formId || ''}`,
    busy: busy || save.kind === 'saving',
    what: `Your edits to ${form?.name || 'this form'}`,
    onSave: () => saveDraft(),
    saveLabel: 'Save draft and leave',
  });

  // Ctrl/⌘+S saves the draft (R3, Appendix B): the browser's "save page" is
  // never what somebody editing a form means, so it is always taken; with
  // nothing to save it does nothing. From inside a field too — a save is
  // what you want mid-sentence.
  const stateRef = useRef(state);
  stateRef.current = state;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.altKey || e.shiftKey || e.key.toLowerCase() !== 's') return;
      e.preventDefault();
      if (stateRef.current !== 'clean') void runSaveRef.current(true).catch(() => undefined);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  /** Open the Publish panel, with the count worked out for the working copy as it stands. */
  const openPublish = useCallback(() => {
    setPublishOpen(true);
    if (draftRef.current) void refreshChangesRef.current(draftRef.current);
  }, []);

  // Before there is a form to put a bar over, the page is the design's padded
  // page like any other tab (§1a) — never a centred column.
  if (noAccess) return <div className="fw-page"><NoAccessPage formName={noAccess.formName} /></div>;
  if (error) return <div className="fw-page"><div className="ad-card"><div className="ad-err">{error}</div></div></div>;
  if (!form) return <div className="fw-page"><div className="ad-card">Loading…</div></div>;

  const base = `/admin/forms/${form.id}`;
  const tab = location.pathname.replace(/\/+$/, '').split('/')[4] || '';
  const onDefinitionTab = DEFINITION_TABS.includes(tab);
  // An ABSENT level means the server did not say, not that the answer is no:
  // the client only hides doors as a courtesy, and the routes refuse either
  // way. Hiding every editing tab because a field was missing would break the
  // workspace for a reason nobody could see.
  const canEdit = !myLevel || myLevel === 'edit' || myLevel === 'manage';
  const canManage = myLevel === 'manage';
  const publicUrl = form.status === 'published'
    ? `${(publicBaseUrl || window.location.origin).replace(/\/+$/, '')}/f/${accountSlug ? `${accountSlug}/${form.slug}` : form.slug}`
    : null;

  const ctx: FormWorkspaceContext = {
    form, refreshForm, noteDocumentDraft, registerBeforePublish, documentEpoch, features, brand, setBrand,
    draft, ...setters, replaceDraft, history: draftHistory,
    state, save, busy, limitHit,
    version, activity, restoreVersion,
    publicUrl,
  };

  const onSaveClick = () => { void saveDraft().catch(() => undefined); };

  return (
    <div className="fw-wrap">
      <FormBar
        form={form} version={version} activity={activity ?? undefined} myLevel={myLevel}
        canEdit={canEdit} canManage={canManage} base={base} tab={tab} dataSearch={dataSearch}
        publicUrl={publicUrl} phone={phone} indicatorBelow={phone && onDefinitionTab && canEdit}
        state={state} save={save} busy={busy} error={saveError} hasDraft={!!draft}
        changeCount={changes ? changes.count : null}
        onSave={onSaveClick} onConflict={() => setConflictOpen(true)} onPublish={openPublish}
        onGetLink={() => setDistributing(true)} onShare={() => setSharing(true)}
        // "Ask AI" (068): renders nothing unless the install has an AI key.
        assistant={<FormAssistant formId={form.id} draft={draft} onApply={replaceDraft} />}
      />
      {/* A self-framed tab fills the viewport under the bar on its own; any
          other tab gets the design's page padding, edge to edge. */}
      {SELF_FRAMED_TABS.includes(tab) ? <Outlet context={ctx} /> : <div className="fw-page"><Outlet context={ctx} /></div>}
      {draft && phone && onDefinitionTab && canEdit && (
        <ActionBar className="fw-draftbar">
          <UnpublishedIndicator
            form={form} state={state} save={save} error={saveError} changeCount={changes ? changes.count : null}
            onOpen={openPublish} onSave={onSaveClick} onConflict={() => setConflictOpen(true)}
          />
          <span style={{ flex: 1 }} />
          <PhoneDraftMenu
            busy={busy} state={state} saving={save.kind === 'saving'}
            onVersions={() => navigate(`${base}/versions`)} onSave={onSaveClick} onDiscard={() => { void discard(); }}
          />
          <button className="fs-btn primary" disabled={busy} onClick={openPublish}>Publish</button>
        </ActionBar>
      )}
      {sharing && (
        <ShareDialog kind="form" id={form.id} onClose={() => setSharing(false)} onSaved={refreshForm} />
      )}
      {distributing && (
        <DistributeDialog
          form={{ id: form.id, name: form.name, slug: form.slug }} accountSlug={accountSlug}
          publicBaseUrl={publicBaseUrl} platformBaseUrl={platformBaseUrl} accessMode={form.settings.access?.mode}
          onClose={() => setDistributing(false)}
        />
      )}
      {publishOpen && draft && (
        <PublishDrawer
          form={form} version={version} changes={changes} loading={changesLoading} error={changesError}
          state={state} save={save} busy={busy} limitHit={limitHit} versionsBase={`${base}/versions`}
          onClose={() => setPublishOpen(false)}
          onPublish={() => { void publish(); }}
          onTest={() => { setPublishOpen(false); navigate(`${base}/build?test=1`); }}
          onSave={onSaveClick}
          onDiscard={() => { void discard(); }}
          onRestore={(v) => { void restoreVersion(v).then(() => navigate(`${base}/build`)).catch(() => undefined); }}
        />
      )}
      {save.kind === 'conflict' && conflictOpen && (
        <Modal
          narrow
          title="Someone else saved this form"
          onClose={() => setConflictOpen(false)}
          footer={(
            <>
              <button className="fs-btn primary" disabled={busy} onClick={() => { void reloadTheirs(); }}>Reload their version</button>
              <button className="fs-btn neutral" disabled={busy} onClick={keepMine}>Keep mine</button>
            </>
          )}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            Another session saved a draft of <strong>{form.name}</strong>
            {save.theirs ? <> at {new Date(save.theirs).toLocaleTimeString()}</> : null}, after this
            one was opened. Your unsaved changes have not been written.
          </p>
          <p className="fs-cell-sub" style={{ marginTop: 10, lineHeight: 1.6 }}>
            <strong>Reload their version</strong> drops your unsaved changes and shows theirs.
            <strong> Keep mine</strong> writes yours over theirs.
          </p>
        </Modal>
      )}
      {guard.node}
    </div>
  );
}

/**
 * The phone's "This draft" action sheet (D4/F11): Versions, Save draft and
 * Discard, behind one menu beside Publish. Every handler is the workspace's
 * own — nothing about publishing, autosave, the conflict dialog or the
 * unsaved guard is different down here.
 */
function PhoneDraftMenu({ busy, state, saving, onVersions, onSave, onDiscard }: {
  busy: boolean; state: DraftState; saving: boolean;
  onVersions: () => void; onSave: () => void; onDiscard: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <RowMenu open={menuOpen} onToggle={setMenuOpen} label="This draft">
      <button disabled={busy} onClick={onVersions}>Versions…</button>
      <button disabled={busy || state === 'clean' || saving} onClick={onSave}>Save draft</button>
      {state !== 'clean' && (
        <button className="danger" disabled={busy} onClick={onDiscard}>Discard the draft</button>
      )}
    </RowMenu>
  );
}

export function useFormWorkspace(): FormWorkspaceContext {
  return useOutletContext<FormWorkspaceContext>();
}

/**
 * A Member opens a form URL they have no grant on (canvas 3D).
 *
 * Plain, not alarming, and it says the NAME: inside one account, hiding the
 * form's existence buys nothing and costs somebody an hour. There is no
 * Request access button in v1 — the button is one click, the notification path
 * behind it is not — so the page names the Admins instead, which is answerable
 * with the data already on hand.
 */
function NoAccessPage({ formName }: { formName: string }) {
  const [admins, setAdmins] = useState<string[]>([]);
  useEffect(() => { adminMe().then((r) => setAdmins(r.admins || [])).catch(() => undefined); }, []);

  return (
    <div className="fs-card pad" style={{ maxWidth: 560, margin: '48px auto', textAlign: 'center' }}>
      <span className="fs-pill neutral">no access</span>
      <h1 style={{ fontSize: 'var(--text-page-title)', margin: '14px 0 0' }}>
        {formName} hasn&rsquo;t been shared with you
      </h1>
      <div className="fs-cell-sub" style={{ marginTop: 10, lineHeight: 1.6 }}>
        It is a form in this account, but you do not have a grant on it or on the
        folder it lives in. Ask an Admin for access.
      </div>
      <div style={{ marginTop: 18 }}>
        <Link className="fs-btn primary" to="/admin">Back to my forms</Link>
      </div>
      {!!admins.length && (
        <div className="fs-cell-sub faint" style={{ marginTop: 16 }}>
          Admins of this account: {admins.join(', ')}.
        </div>
      )}
    </div>
  );
}
